/**
 * N226 - serie por hora em tres paineis com o mesmo eixo de horas.
 *
 * Requisicoes, tempo e simultaneas tem escalas diferentes: dividir um eixo Y
 * inventaria correlacao, entao cada medida tem o seu painel. `syncId` alinha
 * o hover dos tres na mesma hora.
 */
import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDarkMode } from '../../contexts/DarkModeContext';
import {
  Origem,
  ResumoHora,
  completarHoras,
  formatarHora,
  formatarMs,
} from '../../services/diagnosticoNuvemService';

// Paleta conferida no validador de cor (CVD e contraste) contra o card claro
// (#fff) e o escuro (#1f2937). Ordem fixa: app, web, outro. p50/p95 = dois tons
// do mesmo azul (mesma medida); no escuro o tom fraco e o mais escuro.
const CORES = {
  claro: {
    app: '#2a78d6', web: '#eb6834', outro: '#1baf7a', p50: '#86b6ef', p95: '#1c5cab',
    grade: '#e5e7eb', eixo: '#6b7280', superficie: '#ffffff', texto: '#111827',
  },
  escuro: {
    app: '#3987e5', web: '#d95926', outro: '#199e70', p50: '#256abf', p95: '#86b6ef',
    grade: '#374151', eixo: '#9ca3af', superficie: '#1f2937', texto: '#f3f4f6',
  },
};
// Cor de status (fila), nunca de serie; vai junto com a linha rotulada.
const CRITICO = '#d03b3b';

interface GraficoPorHoraProps {
  dados: ResumoHora[];
  lugares: number | null;
  /** filtro da tela: as simultaneas sao sempre de todas as origens (o lugar e compartilhado) */
  origem: Origem | '';
}

const Painel = ({ titulo, nota, children }: { titulo: string; nota?: string; children: React.ReactElement }) => (
  <div>
    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{titulo}</h4>
      {nota && <span className="text-xs text-gray-500 dark:text-gray-400">{nota}</span>}
    </div>
    <ResponsiveContainer width="100%" height={200}>
      {children}
    </ResponsiveContainer>
  </div>
);

export const GraficoPorHora = ({ dados, lugares, origem }: GraficoPorHoraProps) => {
  const { isDarkMode } = useDarkMode();
  const c = isDarkMode ? CORES.escuro : CORES.claro;
  const serie = completarHoras(dados);

  const grade = <CartesianGrid vertical={false} stroke={c.grade} />;
  const eixoX = (
    <XAxis dataKey="hora" tickFormatter={formatarHora} stroke={c.eixo} fontSize={11} minTickGap={24} />
  );
  const dica = {
    labelFormatter: (hora: string) => formatarHora(hora),
    contentStyle: { backgroundColor: c.superficie, borderColor: c.grade, color: c.texto, fontSize: 12 },
    cursor: { fill: c.grade, opacity: 0.4 },
  };
  // texto da legenda na cor de texto; a cor da serie fica so no marcador
  const legenda = (
    <Legend wrapperStyle={{ fontSize: 12 }} formatter={(nome) => <span style={{ color: c.eixo }}>{nome}</span>} />
  );
  const margem = { top: 8, right: 16, left: 0, bottom: 0 };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white">Por hora</h3>

      <Painel titulo="Requisições por hora (por origem)">
        <BarChart data={serie} syncId="n226-por-hora" margin={margem}>
          {grade}
          {eixoX}
          <YAxis stroke={c.eixo} fontSize={11} allowDecimals={false} width={48} />
          <Tooltip {...dica} />
          {legenda}
          {/* stroke da cor do card separa os segmentos empilhados sem desenhar borda */}
          <Bar dataKey="app" name="App" stackId="origem" fill={c.app} stroke={c.superficie} strokeWidth={1} />
          <Bar dataKey="web" name="Web" stackId="origem" fill={c.web} stroke={c.superficie} strokeWidth={1} />
          <Bar dataKey="outro" name="Outro" stackId="origem" fill={c.outro} stroke={c.superficie} strokeWidth={1} />
        </BarChart>
      </Painel>

      <Painel titulo="Tempo de resposta por hora">
        <LineChart data={serie} syncId="n226-por-hora" margin={margem}>
          {grade}
          {eixoX}
          <YAxis stroke={c.eixo} fontSize={11} width={64} tickFormatter={(v) => formatarMs(Number(v))} />
          <Tooltip {...dica} formatter={(v) => formatarMs(Number(v))} />
          {legenda}
          <Line dataKey="p50_ms" name="p50" stroke={c.p50} strokeWidth={2} dot={false} />
          <Line dataKey="p95_ms" name="p95" stroke={c.p95} strokeWidth={2} dot={false} />
        </LineChart>
      </Painel>

      <Painel
        titulo={`Pico de requisições simultâneas por hora${origem ? ' (todas as origens)' : ''}`}
        nota={
          lugares !== null
            ? `linha = ${lugares} lugares (workers × threads); vermelho = requisições na fila`
            : undefined
        }
      >
        <BarChart data={serie} syncId="n226-por-hora" margin={margem}>
          {grade}
          {eixoX}
          {/* o teto do eixo inclui os lugares: a linha aparece mesmo num dia calmo */}
          <YAxis
            stroke={c.eixo}
            fontSize={11}
            allowDecimals={false}
            width={48}
            domain={[0, (max: number) => Math.max(max, lugares ?? 0)]}
          />
          <Tooltip {...dica} />
          {lugares !== null && (
            <ReferenceLine
              y={lugares}
              stroke={CRITICO}
              strokeDasharray="4 4"
              label={{ value: `${lugares} lugares`, position: 'insideTopRight', fill: c.eixo, fontSize: 11 }}
            />
          )}
          <Bar dataKey="simultaneas_max" name="Simultâneas">
            {serie.map((h) => (
              <Cell
                key={h.hora}
                fill={lugares !== null && h.simultaneas_max >= lugares ? CRITICO : c.app}
              />
            ))}
          </Bar>
        </BarChart>
      </Painel>
    </div>
  );
};
